import React, { useEffect, useRef, useState } from "react";
import type { ComponentProps, PartData } from "@enonic/react-components";
import type { CaptchaInfo, FormField } from "./FormViewProcessor";

interface FormViewProps {
  submitText?: string;
  successMessage?: string;
  fields?: FormField[];
  actionUrl?: string;
  error?: string;
  captcha?: CaptchaInfo;
}

const renderField = (field: FormField) => {
  const id = `form-field-${field.name}`;

  if (field.type === "textarea") {
    return (
      <div key={field.name} className="form-view__field">
        <label htmlFor={id} className="form-view__label">
          {field.label}
          {field.required && <span className="form-view__required">*</span>}
        </label>
        <textarea
          id={id}
          name={field.name}
          className="form-view__textarea"
          placeholder={field.placeholder}
          defaultValue={field.defaultValue}
          required={field.required}
          rows={5}
        />
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div key={field.name} className="form-view__field form-view__field--checkbox">
        <label className="form-view__checkbox-label">
          <input
            type="checkbox"
            name={field.name}
            className="form-view__checkbox"
            required={field.required}
          />
          {field.richText ? (
            <span
              className="form-view__checkbox-text"
              dangerouslySetInnerHTML={{ __html: field.richText }}
            />
          ) : (
            <span className="form-view__checkbox-text">{field.label}</span>
          )}
          {field.required && <span className="form-view__required">*</span>}
        </label>
      </div>
    );
  }

  if (field.type === "checkboxes") {
    return (
      <fieldset key={field.name} className="form-view__field form-view__field--group">
        <legend className="form-view__label">
          {field.label}
          {field.required && <span className="form-view__required">*</span>}
        </legend>
        {(field.options ?? []).map((opt) => (
          <label key={opt.value} className="form-view__option-label">
            <input
              type="checkbox"
              name={field.name}
              value={opt.value}
              className="form-view__checkbox"
              defaultChecked={opt.checked}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </fieldset>
    );
  }

  if (field.type === "radio") {
    return (
      <fieldset key={field.name} className="form-view__field form-view__field--group">
        <legend className="form-view__label">
          {field.label}
          {field.required && <span className="form-view__required">*</span>}
        </legend>
        {(field.options ?? []).map((opt) => (
          <label key={opt.value} className="form-view__option-label">
            <input
              type="radio"
              name={field.name}
              value={opt.value}
              className="form-view__radio"
              defaultChecked={opt.checked}
              required={field.required}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </fieldset>
    );
  }

  if (field.type === "select") {
    const defaultVal =
      field.options?.find((o) => o.checked)?.value ?? (field.placeholder ? "" : undefined);
    return (
      <div key={field.name} className="form-view__field">
        <label htmlFor={id} className="form-view__label">
          {field.label}
          {field.required && <span className="form-view__required">*</span>}
        </label>
        <select
          id={id}
          name={field.name}
          className="form-view__select"
          required={field.required}
          defaultValue={defaultVal}
        >
          {field.placeholder && (
            <option value="" disabled>
              {field.placeholder}
            </option>
          )}
          {(field.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // text, email, tel, number, url, date, etc.
  return (
    <div key={field.name} className="form-view__field">
      <label htmlFor={id} className="form-view__label">
        {field.label}
        {field.required && <span className="form-view__required">*</span>}
      </label>
      <input
        id={id}
        type={field.type}
        name={field.name}
        className="form-view__input"
        placeholder={field.placeholder}
        defaultValue={field.defaultValue}
        required={field.required}
      />
    </div>
  );
};

export const FormViewPart = (props: ComponentProps<PartData>) => {
  const {
    fields = [],
    submitText = "Send",
    successMessage,
    actionUrl,
    error,
    captcha,
  } = (props.data as FormViewProps) ?? {};

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const captchaRef = useRef<HTMLDivElement>(null);

  // Load Google's reCAPTCHA v2 script when captcha is enabled.
  // Google's api.js auto-renders any <div class="g-recaptcha">.
  useEffect(() => {
    if (!captcha?.enabled || !captcha.siteKey) return;
    if (typeof document === "undefined") return;
    if (document.querySelector('script[data-recaptcha-loader]')) return;
    const s = document.createElement("script");
    s.src = "https://www.google.com/recaptcha/api.js";
    s.async = true;
    s.defer = true;
    s.setAttribute("data-recaptcha-loader", "true");
    document.head.appendChild(s);
  }, [captcha?.enabled, captcha?.siteKey]);

  // Fit Google's fixed-width (~302px) reCAPTCHA widget to its container so it
  // is never cut off. api.js renders it asynchronously, so we re-fit for a few
  // seconds after mount and on any container resize.
  useEffect(() => {
    if (!captcha?.enabled) return;
    const wrap = captchaRef.current;
    if (!wrap) return;
    const WIDGET_W = 304;
    const WIDGET_H = 78;
    const fit = () => {
      const widget = wrap.querySelector<HTMLElement>(".g-recaptcha");
      if (!widget) return;
      const scale = Math.min(1, wrap.clientWidth / WIDGET_W);
      widget.style.transformOrigin = "0 0";
      widget.style.transform = scale < 1 ? `scale(${scale})` : "";
      wrap.style.height = scale < 1 ? `${WIDGET_H * scale}px` : "";
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(wrap);
    const poll = setInterval(fit, 400);
    const stop = setTimeout(() => clearInterval(poll), 4000);
    return () => {
      ro.disconnect();
      clearInterval(poll);
      clearTimeout(stop);
    };
  }, [captcha?.enabled]);

  if (error === "no-form-selected") {
    return <div className="form-view form-view--notice">Velg et skjema i innstillingene.</div>;
  }
  if (error === "form-not-found") {
    return <div className="form-view form-view--notice">Skjema ikke funnet.</div>;
  }

  if (submitted) {
    return (
      <div className="form-view form-view--success">
        {successMessage ? (
          <div dangerouslySetInnerHTML={{ __html: successMessage }} />
        ) : (
          <p>Takk! Vi svarer deg så snart vi kan.</p>
        )}
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!actionUrl) {
      setSubmitError("Skjema er feilkonfigurert.");
      return;
    }

    const formData = new FormData(e.currentTarget);

    // Client-side captcha gate: refuse to send if the widget hasn't
    // produced a token yet. Server still re-verifies — this is just UX.
    if (captcha?.enabled) {
      const token = String(formData.get("g-recaptcha-response") ?? "");
      if (!token) {
        setSubmitError("Bekreft at du ikke er en robot.");
        return;
      }
    }

    // Send as application/x-www-form-urlencoded, NOT multipart: Enonic XP
    // populates request.params only from urlencoded bodies (multipart must
    // be read via getMultipartForm). This form has no file inputs, so this
    // is correct and lets the server keep reading request.params.
    const body = new URLSearchParams();
    formData.forEach((value, key) => body.append(key, String(value)));

    setSubmitting(true);
    setSubmitError("");

    try {
      const res = await fetch(actionUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
        credentials: "same-origin",
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setSubmitError("Noe gikk galt. Prøv igjen.");
      }
    } catch {
      setSubmitError("Noe gikk galt. Prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-view">
      <form
        className="form-view__form"
        action={actionUrl}
        method="POST"
        onSubmit={handleSubmit}
      >
        {fields.map(renderField)}

        {/* reCAPTCHA widget — Google's api.js finds this by class and
            renders the challenge inside it. The token ends up as a
            hidden input named "g-recaptcha-response" which is included
            in FormData automatically. */}
        {captcha?.enabled && captcha.siteKey && (
          <div className="form-view__field form-view__captcha" ref={captchaRef}>
            <div className="g-recaptcha" data-sitekey={captcha.siteKey}></div>
          </div>
        )}

        {submitError && <p className="form-view__error">{submitError}</p>}

        <div className="form-view__submit-wrap">
          <button
            type="submit"
            className="form-view__submit btn btn--primary"
            disabled={submitting}
          >
            {submitting ? "Sender…" : submitText}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormViewPart;
